import React, { useEffect, useState, useRef, useCallback } from "react";
import PageContainer from "./layout/PageContainer.js";
import ReflectionRichText from "./ReflectionRichText.js";
import BehaviorPatternInsight from "./BehaviorPatternInsight.js";
import EmotionInsightDisplay from "./EmotionInsightDisplay.js";
import { getNetlifyFunctionsBase } from "../utils/netlifyFunctions.js";
import { getStoredReflections } from "../utils/reflectionStorage.js";
import {
  downloadReflectionsJson,
  mapInsightsForExport,
  mapLocalReflectionsForExport
} from "../utils/exportReflections.js";
import { normalizeReflectionEmotion, DEFAULT_EMOTION_WHEN_MISSING, getEmotionMeta } from "../constants/emotions.js";
import { IconThumbDown, IconThumbUp, IconTrash } from "./RatingIcons.js";
import { isQuickClarityInsight } from "../utils/isQuickClarityInsight.js";
import { buildTranslationFieldsFromHistoryItem } from "../utils/insightTranslationFields.js";
import { FREE_HISTORY_VISIBLE } from "../constants/pricing.js";
import { getStoredUserId, historyCacheKeyForUser } from "../utils/userSession.js";

const STRINGS = {
  en: {
    languageGroupAria: "Choose display language",
    pageTitle: "History",
    loadingReflections: "Loading reflections",
    sectionNew: "Quick Clarity",
    sectionLegacy: "Legacy Reflection",
    sectionLegacyHint: "Earlier reflections kept in the previous format.",
    freePlanNote: `Free plan shows your last ${FREE_HISTORY_VISIBLE} Quick Clarity sessions (and up to ${FREE_HISTORY_VISIBLE} legacy reflections).`,
    exportJson: "Export JSON",
    noneYet: "No reflections saved yet.",
    noneInSection: "None in this section.",
    none: "None",
    hide: "Hide",
    view: "View →",
    session: "Session",
    reflection: "Reflection",
    insightSummary: "Insight Summary",
    understanding: "What Ahavibes understood",
    primaryInsight: "Primary insight",
    observedFacts: "What happened",
    unknowns: "What remains unknown",
    thinkingPattern: "Possible thinking pattern",
    clarifyingQuestion: "Need one more detail",
    beneathEmotions: "May also be present",
    supportMessage: "Support",
    emotion: "Emotion",
    category: "Category",
    event: "What happened with you",
    interpretation: "Interpretation",
    evidence: "Evidence",
    response: "Response",
    legacyFormatNote: "This reflection uses an older format; new insights use structured fields.",
    like: "Like",
    dislike: "Dislike",
    delete: "Delete",
    noReflectionText: "No reflection text.",
    translating: "Translating…",
    translateHint: "This page uses AI translation; check important details in English if needed.",
    translationFailed: "Could not load translation for this card. Try again or switch to English.",
    retryTranslation: "Retry translation"
  },
  hi: {
    languageGroupAria: "प्रदर्शन भाषा चुनें",
    pageTitle: "पिछले चिंतन",
    loadingReflections: "चिंतन लोड हो रहे हैं",
    sectionNew: "त्वरित स्पष्टता",
    sectionLegacy: "पूर्व चिंतन",
    sectionLegacyHint: "पुराने प्रारूप में रखे गए चिंतन।",
    freePlanNote: "मुफ्त योजना में आपके अंतिम 3 चिंतन दिखते हैं।",
    noneYet: "अभी तक कोई चिंतन सहेजा नहीं गया।",
    noneInSection: "इस खंड में कोई नहीं।",
    none: "कोई नहीं",
    hide: "छिपाएँ",
    view: "देखें →",
    session: "सत्र",
    reflection: "चिंतन",
    insightSummary: "अंतर्दृष्टि सारांश",
    understanding: "आहावाइब्स ने क्या समझा",
    primaryInsight: "मुख्य अंतर्दृष्टि",
    observedFacts: "क्या हुआ",
    unknowns: "अभी अज्ञात",
    thinkingPattern: "संभावित सोच पैटर्न",
    clarifyingQuestion: "एक और विवरण चाहिए",
    beneathEmotions: "ये भी हो सकती हैं",
    supportMessage: "सहायता",
    emotion: "भावना",
    category: "श्रेणी",
    event: "आपके साथ क्या हुआ?",
    interpretation: "व्याख्या",
    evidence: "प्रमाण",
    response: "प्रतिक्रिया",
    legacyFormatNote: "यह चिंतन पुराने प्रारूप में है; नई अंतर्दृष्टि संरचित फ़ील्ड का उपयोग करती है।",
    like: "पसंद",
    dislike: "नापसंद",
    delete: "हटाएँ",
    noReflectionText: "कोई चिंतन पाठ नहीं।",
    translating: "अनुवाद हो रहा है…",
    translateHint: "यह AI अनुवाद है; जरूरी विवरण के लिए अंग्रेज़ी देख सकते हैं।",
    translationFailed: "इस कार्ड का अनुवाद लोड नहीं हुआ। दोबारा कोशिश करें या अंग्रेज़ी चुनें।",
    retryTranslation: "अनुवाद फिर से करें"
  },
  es: {
    languageGroupAria: "Elegir idioma de visualización",
    pageTitle: "Historial de reflexiones",
    loadingReflections: "Cargando reflexiones",
    sectionNew: "Claridad rápida",
    sectionLegacy: "Reflexión anterior",
    sectionLegacyHint: "Reflexiones anteriores en el formato previo.",
    freePlanNote: "El plan gratuito muestra tus últimas 3 reflexiones.",
    noneYet: "Aún no hay reflexiones guardadas.",
    noneInSection: "Ninguna en esta sección.",
    none: "Ninguna",
    hide: "Ocultar",
    view: "Ver →",
    session: "Sesión",
    reflection: "Reflexión",
    insightSummary: "Resumen de ideas",
    understanding: "Lo que Ahavibes entendió",
    primaryInsight: "Idea principal",
    observedFacts: "Qué pasó",
    unknowns: "Lo que sigue sin saberse",
    thinkingPattern: "Posible patrón de pensamiento",
    clarifyingQuestion: "Falta un detalle",
    beneathEmotions: "También pueden estar presentes",
    supportMessage: "Apoyo",
    emotion: "Emoción",
    category: "Categoría",
    event: "¿Qué te pasó?",
    interpretation: "Interpretación",
    evidence: "Evidencia",
    response: "Respuesta",
    legacyFormatNote: "Esta reflexión usa un formato anterior; las nuevas ideas usan campos estructurados.",
    like: "Me gusta",
    dislike: "No me gusta",
    delete: "Eliminar",
    noReflectionText: "Sin texto de reflexión.",
    translating: "Traduciendo…",
    translateHint: "Traducción automática por IA; revisa los detalles importantes en inglés si lo necesitas.",
    translationFailed: "No se pudo cargar la traducción para esta tarjeta. Inténtalo de nuevo o cambia a inglés.",
    retryTranslation: "Reintentar traducción"
  },
  ar: {
    languageGroupAria: "اختر لغة العرض",
    pageTitle: "سجل التأملات",
    loadingReflections: "جاري تحميل التأملات",
    sectionNew: "وضوح سريع",
    sectionLegacy: "تأمل سابق",
    sectionLegacyHint: "تأملات سابقة بالصيغة القديمة.",
    freePlanNote: "الخطة المجانية تعرض آخر 3 تأملات.",
    noneYet: "لا توجد تأملات محفوظة بعد.",
    noneInSection: "لا شيء في هذا القسم.",
    none: "لا شيء",
    hide: "إخفاء",
    view: "عرض ←",
    session: "جلسة",
    reflection: "تأمل",
    insightSummary: "ملخص الرؤى",
    understanding: "ما فهمته أهافايبز",
    primaryInsight: "الرؤية الرئيسية",
    observedFacts: "ما الذي حدث",
    unknowns: "ما يبقى مجهولاً",
    thinkingPattern: "نمط تفكير محتمل",
    clarifyingQuestion: "نحتاج تفصيلاً إضافياً",
    beneathEmotions: "قد تكون حاضرة أيضاً",
    supportMessage: "الدعم",
    emotion: "المشاعر",
    category: "الفئة",
    event: "ما الذي حدث معك؟",
    interpretation: "التفسير",
    evidence: "الأدلة",
    response: "الاستجابة",
    legacyFormatNote: "هذا التأمل يستخدم تنسيقًا أقدم؛ الرؤى الجديدة تستخدم حقولًا منظمة.",
    like: "أعجبني",
    dislike: "لم يعجبني",
    delete: "حذف",
    noReflectionText: "لا نص للتأمل.",
    translating: "جاري الترجمة…",
    translateHint: "الترجمة آلية عبر الذكاء الاصطناعي؛ راجع التفاصيل المهمة بالإنجليزية عند الحاجة.",
    translationFailed: "تعذر تحميل الترجمة لهذه البطاقة. أعد المحاولة أو اختر الإنجليزية.",
    retryTranslation: "إعادة الترجمة"
  },
  fr: {
    languageGroupAria: "Choisir la langue d’affichage",
    pageTitle: "Historique des réflexions",
    loadingReflections: "Chargement des réflexions",
    sectionNew: "Clarté rapide",
    sectionLegacy: "Réflexion antérieure",
    sectionLegacyHint: "Réflexions antérieures au format précédent.",
    freePlanNote: "L’offre gratuite affiche vos 3 dernières réflexions.",
    noneYet: "Aucune réflexion enregistrée pour l’instant.",
    noneInSection: "Aucune dans cette section.",
    none: "Aucune",
    hide: "Masquer",
    view: "Voir →",
    session: "Session",
    reflection: "Réflexion",
    insightSummary: "Synthèse",
    understanding: "Ce qu’Ahavibes a compris",
    primaryInsight: "Insight principal",
    observedFacts: "Ce qui s’est passé",
    unknowns: "Ce qui reste inconnu",
    thinkingPattern: "Schéma de pensée possible",
    clarifyingQuestion: "Un détail de plus",
    beneathEmotions: "Peuvent aussi être présentes",
    supportMessage: "Soutien",
    emotion: "Émotion",
    category: "Catégorie",
    event: "Qu’est-ce qui vous est arrivé ?",
    interpretation: "Interprétation",
    evidence: "Preuves",
    response: "Réponse",
    legacyFormatNote: "Cette réflexion utilise un ancien format ; les nouvelles idées utilisent des champs structurés.",
    like: "J’aime",
    dislike: "Je n’aime pas",
    delete: "Supprimer",
    noReflectionText: "Aucun texte de réflexion.",
    translating: "Traduction en cours…",
    translateHint: "Traduction automatique (IA) ; vérifiez les points importants en anglais si besoin.",
    translationFailed: "Impossible de charger la traduction pour cette carte. Réessayez ou repassez à l’anglais.",
    retryTranslation: "Réessayer la traduction"
  }
};
const LANG_OPTIONS = [
  { code: "en", label: "English" },
  { code: "hi", label: "हिन्दी" },
  { code: "es", label: "Español" },
  { code: "ar", label: "العربية" },
  { code: "fr", label: "Français" }
];

function tCacheKey(itemId, lang) {
  return `${itemId}::${lang}`;
}

const ReflectionHistorySkeleton = ({ labelLoading }) => (
  <div
    className="history-page__skeleton"
    aria-busy="true"
    aria-live="polite"
    aria-label={labelLoading}
  >
    <span className="action-follow-through__sr-only">{labelLoading}</span>
    <div className="history-list history-list--skeleton">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="history-card history-card--skeleton" aria-hidden="true">
          <div className="history-card__top history-skeleton__top">
            <div className="history-skeleton__date skeleton-shimmer" />
            <div className="history-skeleton__btn skeleton-shimmer" />
          </div>
          <div className="history-skeleton__preview">
            <div className="history-skeleton__line history-skeleton__line--long skeleton-shimmer" />
            <div className="history-skeleton__line history-skeleton__line--mid skeleton-shimmer" />
            <div className="history-skeleton__line history-skeleton__line--short skeleton-shimmer" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const buildTranslationFields = buildTranslationFieldsFromHistoryItem;

const DATE_LOCALE_BY_LANG = {
  en: undefined,
  hi: "hi-IN",
  es: "es",
  ar: "ar",
  fr: "fr-FR"
};

const VALID_UI_LANG = new Set(LANG_OPTIONS.map((o) => o.code));

const ReflectionHistory = ({ isSubscriber, verifiedEmail }) => {
  const [reflections, setReflections] = useState([]);
  const [allInsightsRaw, setAllInsightsRaw] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedEntries, setExpandedEntries] = useState({});
  /** Per insight id: display language for that card only */
  const [cardLangById, setCardLangById] = useState({});
  const [translationCache, setTranslationCache] = useState({});
  const [translationErrors, setTranslationErrors] = useState({});
  const inFlightRef = useRef(new Set());

  const tPage = STRINGS.en;

  const langFor = useCallback((itemId) => cardLangById[itemId] || "en", [cardLangById]);

  useEffect(() => {
    const load = async () => {
      if (!verifiedEmail) {
        setReflections([]);
        setAllInsightsRaw([]);
        setCardLangById({});
        setIsLoading(false);
        return;
      }
      setReflections([]);
      setAllInsightsRaw([]);
      setCardLangById({});
      try {
        const response = await fetch(`${getNetlifyFunctionsBase()}/.netlify/functions/getUserInsights`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: verifiedEmail })
        });
        if (!response.ok) {
          const errText = await response.text();
          let msg = "Unable to load reflections.";
          try {
            const parsed = JSON.parse(errText);
            if (parsed?.error) msg = parsed.error;
          } catch (_) {
            if (errText && errText.length < 200) msg = errText;
          }
          throw new Error(msg);
        }
        const data = await response.json();
        const scopedUserId = data?.userId || getStoredUserId();
        const scopedCacheKey = historyCacheKeyForUser(scopedUserId);
        const supabaseItems = Array.isArray(data.insights)
          ? data.insights.map((item) => ({
              id: item.id,
              createdAt: item.created_at,
              reflection: item.entry_text,
              eventReflection: item.insight_json?.event_reflection || "",
              interpretationReflection: item.insight_json?.interpretation_reflection || "",
              evidenceReflection: item.insight_json?.evidence_reflection || "",
              responseStatement: item.insight_json?.response_statement || "",
              behaviorPattern: item.insight_json?.behavior_pattern || "",
              behaviorAction: item.insight_json?.behavior_action || "",
              sentiment: item.insight_json?.sentiment || item.sentiment || "",
              emotionCategory: item.insight_json?.emotion_category || "",
              understanding: item.insight_json?.understanding || "",
              primaryInsight: item.insight_json?.primary_insight || "",
              userInterpretation: item.insight_json?.user_interpretation || "",
              clarifyingQuestion: item.insight_json?.clarifying_question || "",
              observedFacts: Array.isArray(item.insight_json?.observed_or_reported_facts)
                ? item.insight_json.observed_or_reported_facts
                : [],
              unknowns: Array.isArray(item.insight_json?.unknowns) ? item.insight_json.unknowns : [],
              patternLabel: item.insight_json?.possible_cbt_pattern?.label || "",
              patternWhy: item.insight_json?.possible_cbt_pattern?.why_it_may_apply || "",
              emotionBeneath: Array.isArray(item.insight_json?.emotions?.beneath)
                ? item.insight_json.emotions.beneath
                    .map((b) => (typeof b === "string" ? b : b?.label))
                    .filter(Boolean)
                : [],
              supportMessage: item.insight_json?.support_message || "",
              isQuickClarity: isQuickClarityInsight(item.insight_json),
              rating: item.rating || null
            }))
          : [];
        setAllInsightsRaw(Array.isArray(data.insights) ? data.insights : []);
        // Prefer API access flag over a possibly stale prop so Member history is not trimmed.
        const hasFullHistory = data?.isSubscriber === true || isSubscriber === true;
        let visible;
        if (hasFullHistory) {
          visible = supabaseItems;
        } else {
          // Free plan: keep Quick Clarity visible in its own section (do not let legacy fill the window).
          const qc = supabaseItems.filter((r) => r.isQuickClarity).slice(0, FREE_HISTORY_VISIBLE);
          const legacy = supabaseItems.filter((r) => !r.isQuickClarity).slice(0, FREE_HISTORY_VISIBLE);
          visible = [...qc, ...legacy];
        }
        setReflections(visible);
        if (scopedUserId) {
          localStorage.setItem(scopedCacheKey, JSON.stringify(visible));
        }
      } catch (err) {
        console.error("ReflectionHistory load error:", err);
        setError(err?.message || "Unable to load reflections right now.");
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [verifiedEmail, isSubscriber]);

  const translateItem = useCallback(async (itemId, fields, targetLang) => {
    const key = tCacheKey(itemId, targetLang);
    if (!fields || Object.keys(fields).length === 0) {
      setTranslationCache((prev) => ({ ...prev, [key]: {} }));
      return;
    }
    try {
      const response = await fetch(`${getNetlifyFunctionsBase()}/.netlify/functions/translateHistoryContent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fields, targetLang })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || "Translation failed");
      }
      setTranslationCache((prev) => ({ ...prev, [key]: data.translations || {} }));
      setTranslationErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } catch (err) {
      console.error("translateItem", err);
      setTranslationErrors((prev) => ({ ...prev, [key]: err?.message || "error" }));
      setTranslationCache((prev) => ({ ...prev, [key]: {} }));
    }
  }, []);

  const retryTranslation = useCallback(
    (itemId, lang) => {
      if (lang === "en") return;
      const key = tCacheKey(itemId, lang);
      setTranslationErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setTranslationCache((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      inFlightRef.current.delete(key);
      const item = reflections.find((r) => r.id === itemId);
      if (!item) return;
      const fields = buildTranslationFields(item);
      if (Object.keys(fields).length === 0) return;
      inFlightRef.current.add(key);
      translateItem(item.id, fields, lang).finally(() => {
        inFlightRef.current.delete(key);
      });
    },
    [reflections, translateItem]
  );

  const requestTranslation = useCallback(
    (item, lang) => {
      if (!item || lang === "en") return;
      const cKey = tCacheKey(item.id, lang);
      if (translationCache[cKey] !== undefined || inFlightRef.current.has(cKey)) return;
      const fields = buildTranslationFields(item);
      if (Object.keys(fields).length === 0) {
        setTranslationCache((prev) => ({ ...prev, [cKey]: {} }));
        return;
      }
      inFlightRef.current.add(cKey);
      translateItem(item.id, fields, lang).finally(() => {
        inFlightRef.current.delete(cKey);
      });
    },
    [translateItem, translationCache]
  );

  const setCardLang = (itemId, code) => {
    if (!VALID_UI_LANG.has(code)) return;
    setCardLangById((prev) => {
      const next = { ...prev };
      if (code === "en") {
        delete next[itemId];
      } else {
        next[itemId] = code;
      }
      return next;
    });
    const item = reflections.find((r) => r.id === itemId);
    if (code !== "en" && item) {
      requestTranslation(item, code);
    }
  };

  const updateRating = async (id, rating) => {
    setReflections((prev) => prev.map((item) => (item.id === id ? { ...item, rating } : item)));
    if (!verifiedEmail) return;
    try {
      const response = await fetch(`${getNetlifyFunctionsBase()}/.netlify/functions/updateInsightRating`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ insightId: id, rating, email: verifiedEmail })
      });
      if (!response.ok) {
        const text = await response.text();
        console.error("Unable to update rating", response.status, text);
      }
    } catch (err) {
      console.error("Unable to update rating", err);
    }
  };

  const deleteInsight = async (id) => {
    setReflections((prev) => prev.filter((item) => item.id !== id));
    if (!verifiedEmail) return;
    try {
      await fetch(`${getNetlifyFunctionsBase()}/.netlify/functions/deleteInsight`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ insightId: id, email: verifiedEmail })
      });
    } catch (err) {
      console.error("Unable to delete insight", err);
    }
  };

  const tx = (itemId, key, original, lang) => {
    if (lang === "en") return original;
    const block = translationCache[tCacheKey(itemId, lang)];
    if (!block || block[key] == null || block[key] === "") return original;
    return block[key];
  };

  const txList = (itemId, key, originalList, lang) => {
    if (lang === "en" || !Array.isArray(originalList)) return originalList;
    const block = translationCache[tCacheKey(itemId, lang)];
    const translated = block?.[key];
    if (!Array.isArray(translated) || translated.length !== originalList.length) return originalList;
    return translated;
  };

  const toggleEntry = (id) => {
    setExpandedEntries((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const [syncNotice, setSyncNotice] = useState("");
  const [legacyOpen, setLegacyOpen] = useState(false);

  useEffect(() => {
    const notice = sessionStorage.getItem("ahavibes_sync_notice");
    if (notice) {
      setSyncNotice(notice);
      sessionStorage.removeItem("ahavibes_sync_notice");
    }
  }, []);

  const handleExport = async () => {
    if (verifiedEmail && allInsightsRaw.length > 0) {
      downloadReflectionsJson(mapInsightsForExport(allInsightsRaw));
      return;
    }
    const local = await getStoredReflections(getStoredUserId() || undefined);
    if (local.length > 0) {
      downloadReflectionsJson(mapLocalReflectionsForExport(local));
    }
  };

  const newReflections = reflections.filter((r) => r.isQuickClarity);
  const legacyReflections = reflections.filter((r) => !r.isQuickClarity);

  const renderHistoryCard = (item) => {
    const cardLang = langFor(item.id);
    const tCard = STRINGS[cardLang] || STRINGS.en;
    const dateLocale = DATE_LOCALE_BY_LANG[cardLang];
    const emotionEn = normalizeReflectionEmotion((item.sentiment || "").trim() || DEFAULT_EMOTION_WHEN_MISSING);
    const emotionDisplay =
      cardLang !== "en" ? tx(item.id, "emotionLabel", emotionEn, cardLang) : emotionEn;
    const hasStructured =
      !!(item.eventReflection || item.interpretationReflection || item.evidenceReflection || item.responseStatement);
    const previewText = tx(item.id, "reflection", item.reflection || "", cardLang);
    const cacheKey = tCacheKey(item.id, cardLang);
    const isTranslating =
      cardLang !== "en" &&
      translationCache[cacheKey] === undefined &&
      Object.keys(buildTranslationFields(item)).length > 0;

    return (
      <div className="history-card" key={item.id}>
        <div className="history-card__top">
          <span className="history-date">
            {new Date(item.createdAt).toLocaleDateString(dateLocale, {
              year: "numeric",
              month: "short",
              day: "numeric"
            })}
          </span>
          <div className="history-card__top-actions">
            <label className="history-card__lang-label">
              <span className="action-follow-through__sr-only">{tCard.languageGroupAria}</span>
              <select
                className="history-card__lang"
                aria-label={tCard.languageGroupAria}
                value={cardLang}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  e.stopPropagation();
                  setCardLang(item.id, e.target.value);
                }}
              >
                {LANG_OPTIONS.map(({ code, label }) => (
                  <option key={code} value={code}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <button type="button" className="history-card__view" onClick={() => toggleEntry(item.id)}>
              {expandedEntries[item.id] ? tCard.hide : tCard.view}
            </button>
          </div>
        </div>
        <div
          className="history-card__locale"
          dir={cardLang === "ar" ? "rtl" : "ltr"}
          lang={cardLang}
        >
          {isTranslating ? <p className="history-card__translating">{tCard.translating}</p> : null}
          {translationErrors[cacheKey] ? (
            <div className="history-card__translate-error-wrap">
              <p className="history-card__translate-error">{tCard.translationFailed}</p>
              <button
                type="button"
                className="history-card__retry-translate"
                onClick={() => retryTranslation(item.id, cardLang)}
              >
                {tCard.retryTranslation}
              </button>
            </div>
          ) : null}
          {cardLang !== "en" ? <p className="history-card__translate-hint">{tCard.translateHint}</p> : null}
          {!expandedEntries[item.id] ? (
            <div className="entry-preview entry-preview--clamp">
              {previewText || tCard.noReflectionText}
            </div>
          ) : (
            <div className="history-card__details">
              <h3>{tCard.session}</h3>
              <p className="entry-full-text">{tx(item.id, "reflection", item.reflection || "—", cardLang)}</p>
              {item.isQuickClarity ? (
                <div className="history-reflection-block">
                  <EmotionInsightDisplay
                    sentiment={item.sentiment}
                    emotionCategory={item.emotionCategory}
                    emotionLabel={tCard.emotion}
                    categoryLabel={tCard.category}
                    translatedEmotionLabel={emotionDisplay}
                    translatedCategory={
                      cardLang !== "en" ? tx(item.id, "emotionCategory", item.emotionCategory || "", cardLang) : null
                    }
                    translatedMeaning={
                      cardLang !== "en"
                        ? tx(
                            item.id,
                            "emotionMeaning",
                            getEmotionMeta(emotionEn)?.meaning || "",
                            cardLang
                          )
                        : null
                    }
                  />
                  {item.emotionBeneath?.length > 0 ? (
                    <div>
                      <strong>{tCard.beneathEmotions}:</strong>
                      <ul className="history-qc-list">
                        {txList(item.id, "emotionBeneath", item.emotionBeneath, cardLang).map((label) => (
                          <li key={label}>{label}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  {item.understanding ? (
                    <p>
                      <strong>{tCard.understanding}:</strong>{" "}
                      {tx(item.id, "understanding", item.understanding, cardLang)}
                    </p>
                  ) : null}
                  {item.observedFacts?.length > 0 ? (
                    <div>
                      <strong>{tCard.observedFacts}:</strong>
                      <ul className="history-qc-list">
                        {txList(item.id, "observedFacts", item.observedFacts, cardLang).map((fact) => (
                          <li key={fact}>{fact}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  {item.userInterpretation ? (
                    <p>
                      <strong>{tCard.interpretation}:</strong>{" "}
                      {tx(item.id, "userInterpretation", item.userInterpretation, cardLang)}
                    </p>
                  ) : null}
                  {item.unknowns?.length > 0 ? (
                    <div>
                      <strong>{tCard.unknowns}:</strong>
                      <ul className="history-qc-list">
                        {txList(item.id, "unknowns", item.unknowns, cardLang).map((u) => (
                          <li key={u}>{u}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  {item.primaryInsight ? (
                    <p>
                      <strong>{tCard.primaryInsight}:</strong>{" "}
                      {tx(item.id, "primaryInsight", item.primaryInsight, cardLang)}
                    </p>
                  ) : null}
                  {item.clarifyingQuestion ? (
                    <p>
                      <strong>{tCard.clarifyingQuestion}:</strong>{" "}
                      {tx(item.id, "clarifyingQuestion", item.clarifyingQuestion, cardLang)}
                    </p>
                  ) : null}
                  {item.patternLabel ? (
                    <div>
                      <strong>{tCard.thinkingPattern}:</strong>
                      <p>{tx(item.id, "patternLabel", item.patternLabel, cardLang)}</p>
                      {item.patternWhy ? (
                        <p>{tx(item.id, "patternWhy", item.patternWhy, cardLang)}</p>
                      ) : null}
                    </div>
                  ) : null}
                  {item.supportMessage ? (
                    <p>
                      <strong>{tCard.supportMessage}:</strong>{" "}
                      {tx(item.id, "supportMessage", item.supportMessage, cardLang)}
                    </p>
                  ) : null}
                </div>
              ) : hasStructured ? (
                <div className="history-reflection-block">
                  <h3>{tCard.reflection}</h3>
                  <EmotionInsightDisplay
                    sentiment={item.sentiment}
                    emotionCategory={item.emotionCategory}
                    emotionLabel={tCard.emotion}
                    categoryLabel={tCard.category}
                    translatedEmotionLabel={emotionDisplay}
                    translatedCategory={
                      cardLang !== "en" ? tx(item.id, "emotionCategory", item.emotionCategory || "", cardLang) : null
                    }
                    translatedMeaning={
                      cardLang !== "en"
                        ? tx(
                            item.id,
                            "emotionMeaning",
                            getEmotionMeta(emotionEn)?.meaning || "",
                            cardLang
                          )
                        : null
                    }
                  />
                  {item.behaviorPattern ? (
                    <BehaviorPatternInsight
                      pattern={item.behaviorPattern}
                      action={tx(item.id, "behaviorAction", item.behaviorAction, cardLang)}
                      className="behavior-pattern-insight--history"
                    />
                  ) : null}
                  {item.eventReflection ? (
                    <p>
                      <strong>{tCard.event}:</strong> {tx(item.id, "eventReflection", item.eventReflection, cardLang)}
                    </p>
                  ) : null}
                  {item.interpretationReflection ? (
                    <p>
                      <strong>{tCard.interpretation}:</strong>{" "}
                      {tx(item.id, "interpretationReflection", item.interpretationReflection, cardLang)}
                    </p>
                  ) : null}
                  {item.evidenceReflection ? (
                    <div className="reflection-evidence">
                      <strong>{tCard.evidence}:</strong>
                      <ReflectionRichText
                        className="reflection-multiline"
                        text={tx(item.id, "evidenceReflection", item.evidenceReflection, cardLang)}
                      />
                    </div>
                  ) : null}
                  {item.responseStatement ? (
                    <div className="reflection-response">
                      <strong>{tCard.response}:</strong>
                      <ReflectionRichText
                        className="reflection-multiline"
                        text={tx(item.id, "responseStatement", item.responseStatement, cardLang)}
                      />
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="history-insight-block">
                  <p className="history-card__legacy-note">{tCard.legacyFormatNote}</p>
                  <EmotionInsightDisplay
                    sentiment={item.sentiment}
                    emotionCategory={item.emotionCategory}
                    emotionLabel={tCard.emotion}
                    categoryLabel={tCard.category}
                    translatedEmotionLabel={emotionDisplay}
                    translatedCategory={
                      cardLang !== "en" ? tx(item.id, "emotionCategory", item.emotionCategory || "", cardLang) : null
                    }
                    translatedMeaning={
                      cardLang !== "en"
                        ? tx(
                            item.id,
                            "emotionMeaning",
                            getEmotionMeta(emotionEn)?.meaning || "",
                            cardLang
                          )
                        : null
                    }
                  />
                </div>
              )}
              <div className="rating-buttons">
                <button
                  type="button"
                  className={`rating-button ${item.rating === "like" ? "rating-button--active" : ""}`}
                  onClick={() => updateRating(item.id, "like")}
                  aria-label={tCard.like}
                >
                  <IconThumbUp className="rating-button__icon" />
                </button>
                <button
                  type="button"
                  className={`rating-button ${item.rating === "dislike" ? "rating-button--active" : ""}`}
                  onClick={() => updateRating(item.id, "dislike")}
                  aria-label={tCard.dislike}
                >
                  <IconThumbDown className="rating-button__icon" />
                </button>
                <button
                  className="rating-button rating-button--danger"
                  type="button"
                  onClick={() => deleteInsight(item.id)}
                  aria-label={tCard.delete}
                >
                  <IconTrash className="rating-button__icon" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <PageContainer lang="en" dir="ltr" className="reflection-history-page">
      <h1 className="history-title">{tPage.pageTitle}</h1>
      <div className="history-action-links">
        <button type="button" className="history-action-btn history-action-btn--secondary" onClick={handleExport}>
          {tPage.exportJson}
        </button>
      </div>
      {syncNotice ? <p className="limit-note">{syncNotice}</p> : null}
      {!isSubscriber ? <p className="limit-note">{tPage.freePlanNote}</p> : null}
      {isLoading ? <ReflectionHistorySkeleton labelLoading={tPage.loadingReflections} /> : null}
      {!isLoading && error ? <p className="login-error">{error}</p> : null}
      {!isLoading && !error && reflections.length === 0 ? <p>{tPage.noneYet}</p> : null}
      {!isLoading && !error && reflections.length > 0 ? (
        <>
          <section className="history-section history-section--new" aria-labelledby="history-new-heading">
            <h2 id="history-new-heading" className="history-section__title">
              {tPage.sectionNew}
            </h2>
            {newReflections.length === 0 ? (
              <p className="history-section__empty">{tPage.noneInSection}</p>
            ) : (
              <div className="history-list">{newReflections.map(renderHistoryCard)}</div>
            )}
          </section>
          {legacyReflections.length > 0 ? (
            <section className="history-section history-section--legacy" aria-labelledby="history-legacy-heading">
              <button
                type="button"
                id="history-legacy-heading"
                className="history-section__toggle"
                aria-expanded={legacyOpen}
                onClick={() => setLegacyOpen((v) => !v)}
              >
                <span className="history-section__title history-section__title--legacy">{tPage.sectionLegacy}</span>
                <span className="history-section__toggle-hint">{legacyOpen ? tPage.hide : tPage.view}</span>
              </button>
              {legacyOpen ? (
                <>
                  <p className="history-section__hint">{tPage.sectionLegacyHint}</p>
                  <div className="history-list">{legacyReflections.map(renderHistoryCard)}</div>
                </>
              ) : null}
            </section>
          ) : null}
        </>
      ) : null}
    </PageContainer>
  );
};

export default ReflectionHistory;
